import { z } from "zod";

import { AgentColourNameSchema } from "./agent-colour.schema";
import { providerMetadataSchema } from "./provider-metadata.schema";

/**
 * The failures a reader is told apart, one member per distinct remedy.
 *
 * Deliberately not the provider's vocabulary: the SDK reports an ending through
 * two disjoint namespaces — a result-error `subtype` and a `terminal_reason` —
 * and several of their members leave the reader with the same next move. Three
 * allowances a run can exhaust collapse to `run_limit`; two throttles wearing
 * different names collapse to `rate_limited`; everything carrying no signal this
 * build recognises is `unknown`, with the provider's own words kept verbatim
 * beside it so an operator loses nothing.
 *
 * `rate_limited` and `overloaded` stay apart because one resolves by waiting out
 * a window and the other by sending again now.
 *
 * `runner_fault` is a failure the agent runner raised for itself rather than one
 * the provider reported. It stays apart from `unknown` because the two point the
 * reader at different parties: `unknown` is still the provider's failure, and a
 * runner fault is never told as one.
 */
export const CUE_ERROR_KINDS = [
  "authentication",
  "rate_limited",
  "overloaded",
  "input_too_large",
  "run_limit",
  "runner_fault",
  "unknown"
] as const;

export type CueErrorKind = (typeof CUE_ERROR_KINDS)[number];

/** The longest provider explanation an `error` part keeps — the persisted bound, and the one a live reader holds a failure's detail to as well. */
export const TERMINAL_ERROR_MESSAGE_MAX_LENGTH = 2000;

/**
 * The failure that ended this turn. Always the last part: the run stopped
 * here, so nothing follows it.
 *
 * A turn that fails after producing prose persists that prose, and without
 * this part the row reads on reload as a complete answer — the failure was
 * only ever a live event, and live events are gone by the next page load.
 * Carrying it as a part rather than as a status on the row is what gives it
 * a position: it renders where it happened, after the prose it cut short.
 *
 * Carries no `parentToolUseId`: this ends the lead's turn by definition, and
 * a sub-agent's failure does not end it. Carries no provider bag either —
 * that bag is the home for extras reconstruction replays, and this part is
 * stripped from every reconstruction path, so a bag on it could be read by
 * nobody. The two provider strings that are worth keeping are declared
 * outright instead.
 */
export const terminalErrorPartSchema = z.object({
  type: z.literal("error"),
  /**
   * Which of the provider's failure channels this came from.
   *
   * One value today, and declared anyway: rows are never rewritten, so a
   * `scope` introduced later would be absent on every row written now and
   * could not be told apart from a row that simply predates it. One field
   * costs less than a value that means two things forever.
   */
  scope: z.literal("terminal"),
  errorKind: z.enum(CUE_ERROR_KINDS),
  /**
   * The provider's own explanation, already shown live on this same failure
   * and redacted before it left the host, so persisting it exposes nothing
   * the reader has not already seen.
   */
  message: z.string().max(TERMINAL_ERROR_MESSAGE_MAX_LENGTH).optional(),
  /**
   * The provider's own account of why the turn ended, verbatim — the value
   * `errorKind` was derived from on the API-failure path. Unconstrained,
   * because the provider ships new reasons after this build and a closed set
   * here would reject the whole part rather than the word it did not know.
   */
  terminalReason: z.string().max(64).optional(),
  /**
   * The ending's own result-error subtype, verbatim — the SDK's, on a
   * provider failure, or the runner's own for a fault it raised — and the
   * derivation's provenance on the run-error path. A second field rather
   * than a shared one because `terminalReason` and this are disjoint
   * namespaces, and a single field holding either leaves no reader able to
   * say which it is looking at.
   */
  resultSubtype: z.string().max(64).optional()
});

export type TerminalErrorPart = z.infer<typeof terminalErrorPartSchema>;

/**
 * Persisted ordered content part for an AI message row.
 * Serialized into the `content.parts` JSONB array in stream order.
 * No `state` or `id` — persisted parts are always terminal.
 *
 * 🔴 **A SIGNAL IS NOT A MEMBER HERE, AND ADDING ONE RENDERS IT TWICE.** A
 * signal a run reports about itself persists as its own materialized row keyed
 * on the log entry it folded from — `GenericSignalPart` in
 * `content-part.types.ts` is on the in-flight union and deliberately absent
 * from this one. The reader merges its persisted history with its live rows by
 * ROW ID: live, a signal has always been its own row keyed on its log entry, so
 * as a part it would arrive a second time under the assistant row's id and the
 * merge would keep both. Any refetch draws it twice, and `refetchOnWindowFocus`
 * fires one on an alt-tab.
 *
 * That shape was built and closed unmerged. Its tests passed because they
 * asserted the live half and the reload half separately — each half correct,
 * the pair not.
 *
 * Two further costs came with it: classifying a between-turns signal as
 * "opens a turn" pulled in a billing settle for a generation nobody ran, and it
 * widened a kernel three contexts share for a fact that is display-only. This
 * union is the single, complete, ordered mirror of a turn — replayed to a
 * provider, published inside a turn by the transcript export, and answered by
 * every renderer — so a kind added here is a kind reconstruction must learn to
 * strip. A row is additive to a table; a member here is not.
 */
export const orderedPartSchema = z.union([
  z.object({
    type: z.literal("text"),
    text: z.string(),
    /** Non-null when this part was forwarded from a sub-agent (forwardSubagentText). Absent on rows predating this field. */
    parentToolUseId: z.string().nullable().optional(),
    providerMetadata: providerMetadataSchema.optional()
  }),
  z.object({
    type: z.literal("tool_call"),
    toolCallId: z.string(),
    toolName: z.string(),
    input: z.record(z.string(), z.unknown()),
    /** Non-null when this call was forwarded from a sub-agent. Absent on rows predating this field. */
    parentToolUseId: z.string().nullable().optional(),
    /**
     * The colour the dispatched agent's type declares, for an Agent dispatch
     * whose definition declares one. Persisted rather than re-derived: the
     * definition files live in the sandbox, so nothing off-runner can read them
     * again — a reopened conversation renders from this field alone. Absent on
     * every other tool call, on an agent whose type declares no colour, and on
     * rows predating this field.
     */
    agentColour: AgentColourNameSchema.optional(),
    providerMetadata: providerMetadataSchema.optional()
  }),
  z.object({
    type: z.literal("reasoning"),
    content: z.string(),
    durationMs: z.number().optional(),
    /** Non-null when this part was forwarded from a sub-agent (forwardSubagentText). Absent on rows predating this field. */
    parentToolUseId: z.string().nullable().optional(),
    providerMetadata: providerMetadataSchema.optional()
  }),
  /**
   * Catch-all for a provider block not (yet) modeled. Envelope-aligned: carries the
   * same `parentToolUseId` attribution the modeled variants do; the raw block itself
   * lives in the provider-keyed bag. `providerMetadata` is REQUIRED
   * here since it is the part's normalized-content stand-in.
   */
  z.object({
    type: z.literal("unknown"),
    /** Set when this block was emitted inside a sub-agent's turn; null at top level. Absent on rows predating this field. */
    parentToolUseId: z.string().nullable().optional(),
    providerMetadata: providerMetadataSchema
  }),
  terminalErrorPartSchema
]);

export type OrderedPart = z.infer<typeof orderedPartSchema>;

/** Persisted AI-row `content` JSONB — ordered parts in stream order. Consumer-agnostic. */
export const aiRowContentSchema = z.object({
  parts: z.array(orderedPartSchema)
});

export type AiRowContent = z.infer<typeof aiRowContentSchema>;
